export type PhoneNumberCartItem = {
  id: number;
  phoneNumber: string;
  formattedRegion: string;
  numberType: string;
  pricePerQuarter: string;
  countryISOCode: string;
  region: string | null;
  locality: string | null;
  isFreeNumber: boolean;
};

export type PhoneNumberData = {
  phoneNumber: string;
  formattedRegion: string;
  numberType: string;
  addressRequirement: string;
  pricePerQuarter: string;
  countryISOCode: string;
  region: string;
  locality: string;
};

export type PhoneNumberSearchResult = {
  phoneNumber: string;
  region: string;
  locality: string;
  countryCode: string;
  pricePerQuarter: string;
  addressRequirement: string;
  numberType: string;
};

export type SearchPhoneNumbersPayload = {
  countryCode: string;
  numberType?: string;
  region?: string;
  locality?: string;
};

export type AddPhoneNumbersToCartPayload = {
  phoneNumber: string;
  region: string;
  locality: string;
  countryCode: string;
  pricePerQuarter: string;
  addressRequirement: string;
  numberType: string;
};

export type RemovePhoneNumberFromCartPayload = {
  phoneNumber?: string;
};

export type PurchasePhoneNumbersCartPayload = {
  firstName: string;
  lastName: string;
  companyName: string;
  companyWebsite: string;
  address: string;
  country: string;
  state?: string;
  city?: string;
  postal: string;
  registeredPhoneNumber: string;
};

export type PhoneNumberCartItemFromAPI = {
  id: number;
  phoneNumber: string;
  locality: string;
  countryCode: string;
  pricePerQuarter: string;
  numberType: string;
  region: string;
};

export type CartUniqueConfiguration = {
  countryISOCode: string;
  numberType: string;
  isLocalityRequired: boolean;
};

export type GetPhoneNumbersListPayload = {
  pageNumber?: number;
  pageSize?: number;
  searchText?: string;
  countryCodes?: string[];
  region?: string[];
  numberTypes?: string[];
  teamMemberIds?: number[];
  verificationStatuses?: number[];
  purchaseDateStart?: string;
  purchaseDateEnd?: string;
};

export type PhoneNumberListResponse = {
  pageNumber: number;
  pageSize: number;
  total: number;
  phoneNumbers: PhoneNumber[];
};

export type PhoneNumber = {
  id: number;
  phoneNumber: string;
  isoCountry: string;
  locality: string;
  region: string;
  friendlyName: string;
  numberType: string;
  isPrimaryNumber: boolean;
  isFreeNumber: boolean;
  status: number;
  assignedUser: {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
  } | null;
  callForwarding: boolean;
  callRecording: boolean;
  voicemailGreeting: boolean;
  createdAt: string;
  isSubscriptionToBeCancelled: boolean;
};

export enum DialerPhoneNumberStatus {
  IN_CART = 1,
  PURCHASE_FAILED = 2,
  ACTIVE = 3,
  RELEASE_PENDING = 4,
  RELEASED = 5,
}

export enum PhoneNumberSettingCode {
  VOICEMAIL_URL = 'voicemail-url',
  VOICEMAIL_ENABLED = 'voicemail-enabled',
  CALL_RECORDING_ENABLED = 'call-recording-enabled',
  FORWARDING_ENABLED = 'forwarding-enabled',
  FORWARDING_ALWAYS_ENABLED = 'forwarding-always-enabled',
  FORWARDING_ALWAYS_NUMBER = 'forwarding-always-number',
  FORWARDING_UNANSWERED_ENABLED = 'forwarding-unanswered-enabled',
  FORWARDING_UNANSWERED_SECONDS = 'forwarding-unanswered-seconds',
  FORWARDING_UNANSWERED_NUMBER = 'forwarding-unanswered-number',
  FORWARDING_BUSY_ENABLED = 'forwarding-busy-enabled',
  FORWARDING_BUSY_NUMBER = 'forwarding-busy-number',
  FORWARDING_OOF_ENABLED = 'forwarding-oof-enabled',
  FORWARDING_OOF_NUMBER = 'forwarding-oof-number',
  FORWARDING_OOF_START = 'forwarding-oof-start',
  FORWARDING_OOF_END = 'forwarding-oof-end',
  TRANSCRIPTION_LANGUAGE = 'transcription-language',
}

// Enum for tracking the current phone number settings action for toast messages
export enum PhoneNumberSettingsActionType {
  CALL_RECORDING = 'call-recording',
  VOICEMAIL = 'voicemail',
  CALL_FORWARDING = 'call-forwarding',
}

export type PhoneNumberSetting = {
  code: PhoneNumberSettingCode;
  value: string;
};

export type GetPhoneNumberSettingsResponse = {
  id: number;
  phoneNumber: string;
  friendlyName: string;
  isPrimaryNumber: boolean;
  isFreeNumber: boolean;
  assignedUser: {
    id: number;
    email: string;
    firstName: string;
    lastName: string;
  } | null;
  settings: PhoneNumberSetting[];
};

export type PhoneNumberListingFilters = {
  searchText?: string;
  countryCodes?: string[];
  numberTypes?: string[];
  teamMemberIds?: number[];
  verificationStatuses?: number[];
  purchaseDateStart?: string;
  purchaseDateEnd?: string;
  regions?: {
    isoCountry: string;
    region: string;
    locality: string;
  }[];
};

export type TeamMembersList = {
  id: number;
  name: string;
  email: string;
};
